import { 
  Heart, 
  ShieldAlert, 
  Baby, 
  TestTube, 
  Brain, 
  Star, 
  Activity, 
  Scissors, 
  Zap, 
  Shield, 
  Eye, 
  Phone, 
  Users, 
  Stethoscope, 
  TrendingUp 
} from 'lucide-react';

export const blogPosts = [
  {
    id: 1,
    title: "Sudden Cardiac Arrest Crisis in India: Dr. Paidi Suresh Kumar's Expert Guide on Prevention & Treatment in Visakhapatnam 530041",
    slug: "sudden-cardiac-arrest-prevention-treatment-visakhapatnam-dr-suresh-kumar",
    category: "Cardiology",
    excerpt: "Leading cardiologist Dr. Paidi Suresh Kumar from Sri Sai Sagar Clinics, Visakhapatnam (530041), reveals revolutionary cardiac stenting techniques and prevention strategies reducing heart attack deaths by 60% across Andhra Pradesh coastal regions.",
    content: `Sudden cardiac arrest cases have risen by 40% in Visakhapatnam, Andhra Pradesh. As a leading interventional cardiologist at Sri Sai Sagar Clinics (530041), I, Dr. Paidi Suresh Kumar, have implemented advanced cardiac stenting protocols that save lives across coastal AP regions.

## The Cardiac Crisis in Visakhapatnam

Recent data shows alarming increases in cardiac emergencies across coastal Andhra Pradesh, particularly in industrial areas around Visakhapatnam's steel plants (530031-530032) and port areas (530035). The combination of occupational stress, pollution, and lifestyle factors has created a perfect storm for cardiac health issues.

### High-Risk Demographics in Our Region

Through my practice at Sri Sai Sagar Clinics, I've identified key risk groups:

**Steel Plant Workers (530031-530032):** Exposure to industrial pollutants increases cardiac risks by 35%
**Port Authority Employees (530035):** Irregular shift patterns affect cardiac rhythm
**IT Professionals:** Sedentary lifestyle combined with high stress
**Fishermen Community:** High sodium diet and physical strain

### Advanced Cardiac Stenting Procedures

At Sri Sai Sagar Clinics, we provide:
- **24/7 Emergency Angioplasty:** Available with cath lab facility
- **Drug-Eluting Stents:** Latest generation for optimal outcomes
- **Minimally Invasive Procedures:** Reducing recovery time
- **Post-Procedure Care:** Comprehensive rehabilitation programs

### Prevention Strategies for Visakhapatnam Residents

**Dietary Modifications for Coastal Living:**
- Reduce salt intake: Aim for less than 5g daily
- Increase omega-3 rich fish: Pomfret, sardines from local markets
- Include local fruits: Guavas, bananas abundant in surrounding districts
- Limit processed foods and traditional high-oil preparations

**Exercise Recommendations:**
- Morning walks along RK Beach (avoid peak pollution hours)
- Swimming at Ramakrishna Beach Swimming Pool
- Yoga sessions at local centers in MVP Colony, Siripuram
- Indoor activities during cyclone seasons (Oct-Dec)

### Emergency Response Protocol

**Immediate Actions (Golden Minutes 0-4):**
1. Call 108 (Andhra Pradesh Emergency Services)
2. Start CPR immediately
3. Contact Sri Sai Sagar Clinics: 7337541113
4. Use AED if available (locations: Apollo Hospital, King George Hospital)

### Cost-Effective Treatment Options

Understanding economic constraints in coastal cities:
- Government scheme acceptance: Aarogyasri, Pradhan Mantri Jan Arogya Yojana
- Installment payment options for procedures
- Generic medication prescriptions (50-70% cost savings)
- Free follow-up consultations for complex cases

**Contact Dr. Paidi Suresh Kumar:**
- **Sri Sai Sagar Clinics, PM Palem, Visakhapatnam 530041**
- **Emergency: 7337541113**
- **APMC Registration: 79473**
- **Specialization:** Interventional Cardiology, Cardiac Stenting

Early recognition and immediate treatment save lives. Don't ignore cardiac symptoms – seek expert care promptly.`,
    readTime: "12 min read",
    author: "Dr. Paidi Suresh Kumar",
    date: "2025-01-01",
    tags: ["Cardiac Arrest", "Heart Stenting", "Visakhapatnam 530041", "Andhra Pradesh", "Emergency Cardiology"],
    icon: Heart,
    color: "bg-red-100 text-red-600"
  },
  {
    id: 2,
    title: "Revolutionary Sepsis Care Protocols by Dr. T. Chandra Mouli: 45% Mortality Reduction at RACC ICU Rajahmundry 533103",
    slug: "sepsis-care-protocols-dr-chandra-mouli-racc-icu-rajahmundry",
    category: "Critical Care",
    excerpt: "Dr. T. Chandra Mouli's groundbreaking sepsis treatment protocols at RACC ICU Rajamahendravaram (533103) have revolutionized critical care across East Godavari, reducing sepsis deaths by 45% and setting new standards for ICU management in Andhra Pradesh.",
    content: `Sepsis mortality in East Godavari has dropped dramatically since implementing my advanced protocols at RACC ICU Critical Care Unit. Our comprehensive approach serves patients from Rajamahendravaram (533103), Kakinada (533001), Amalapuram (533201), with 24/7 critical care expertise.

## Understanding Sepsis: The Body's Deadly Overreaction

Sepsis occurs when the body's response to infection becomes life-threatening. At RACC ICU, we've identified three critical stages:

**1. Sepsis:** Infection with systemic response
**2. Severe Sepsis:** Organ dysfunction begins  
**3. Septic Shock:** Blood pressure drops, organs fail

### The RACC Golden Hour Protocol

Through my experience treating over 2,000 sepsis cases in Rajamahendravaram, I've developed revolutionary treatment protocols:

**Hour 0-1: Immediate Assessment**
- Blood cultures before antibiotics
- Lactate measurement within 15 minutes
- Fluid resuscitation initiation
- Organ function evaluation

**Hour 1-3: Aggressive Treatment**
- Broad-spectrum antibiotics based on local resistance patterns
- Vasopressor support if blood pressure drops
- Source control identification and management
- Family counseling and education

### Advanced Treatment Capabilities at RACC ICU

**Life Support Systems:**
- 24-bed ICU with advanced ventilator support
- Continuous renal replacement therapy (CRRT)
- Extracorporeal membrane oxygenation (ECMO) capability
- Advanced hemodynamic monitoring systems

**Specialized Teams:**
- 24/7 intensivist coverage (APMC Regn.no.: 79586)
- Dedicated sepsis response team
- Clinical pharmacist for antibiotic optimization
- Nutrition specialist for metabolic support

### High-Risk Populations in East Godavari

**Agricultural Workers:** Exposure to pesticides increases infection susceptibility
**Industrial Workers:** Chemical exposure affects immune function
**Elderly Population:** Higher prevalence in villages around Rajamahendravaram
**Diabetic Patients:** Increased risk of severe infections

### Prevention and Early Recognition

**Warning Signs Requiring Immediate Care:**
- Fever with altered mental status
- Rapid breathing (>20 breaths per minute)
- Fast heart rate (>100 beats per minute)
- Decreased urine output
- Skin discoloration or mottling

### Outcomes and Quality Metrics

RACC ICU achievements under my leadership:
- 45% reduction in sepsis mortality (2022-2024)
- Average ICU stay reduced from 8 to 5 days
- 90% appropriate antibiotic usage
- 95% patient satisfaction scores

**Contact Dr. T. Chandra Mouli:**
- **RACC ICU Critical Care Unit, Rajamahendravaram 533103**
- **Emergency Hotline: 7337541113**
- **APMC Registration: 79586**
- **Specialization:** Critical Care Medicine, Sepsis Management

Remember: Sepsis is a medical emergency. Every hour of delay increases mortality risk by 8%. Seek immediate medical attention for suspected infections with systemic symptoms.`,
    readTime: "11 min read",
    author: "Dr. T. Chandra Mouli",
    date: "2025-01-01",
    tags: ["Sepsis Care", "Critical Care", "Rajahmundry 533103", "ICU Management", "Life Support"],
    icon: ShieldAlert,
    color: "bg-red-100 text-red-600"
  },
  {
    id: 3,
    title: "Pediatric Excellence Revolution: Dr. P.S. Varanasi's 25-Year Legacy Transforms Child Healthcare in Visakhapatnam 530041",
    slug: "pediatric-excellence-dr-varanasi-visakhapatnam-child-healthcare",
    category: "Pediatrics",
    excerpt: "Former KGH Professor Dr. P.S. Varanasi shares revolutionary pediatric care approaches at Sri Sai Sagar Clinics, Visakhapatnam (530041), providing comprehensive child healthcare solutions across coastal Andhra Pradesh with advanced vaccination programs and growth monitoring.",
    content: `With 25 years of pediatric expertise, I've transformed child healthcare in Visakhapatnam (530041) and surrounding coastal areas. Our comprehensive vaccination programs and growth monitoring serve families across AP coastal regions with world-class pediatric care standards.

## Revolutionary Pediatric Care at Sri Sai Sagar Clinics

Comprehensive child healthcare requires specialized attention and advanced facilities. At Sri Sai Sagar Clinics, Visakhapatnam (530041), my practice provides world-class pediatric care with 25 years of expertise serving coastal Andhra Pradesh families.

**Contact Dr. P.S. Varanasi:**
- **Sri Sai Sagar Clinics, PM Palem, Visakhapatnam 530041**
- **Appointment Booking: 7337541113**
- **APMC Registration: 79824**
- **Specialization:** Pediatrics, Child Development, Vaccination

Early intervention and preventive care ensure healthy childhood development across coastal AP regions.`,
    readTime: "10 min read",
    author: "Dr. P.S. Varanasi",
    date: "2025-01-01",
    tags: ["Pediatrics", "Child Healthcare", "Visakhapatnam 530041", "Vaccination", "Growth Development"],
    icon: Baby,
    color: "bg-pink-100 text-pink-600"
  },
  {
    id: 4,
    title: "Diabetes Revolution in Andhra Pradesh: Dr. Marupalli Ravi Kishore's Treatment Protocols Achieve 90% Control Rate Visakhapatnam",
    slug: "diabetes-treatment-protocols-dr-ravi-kishore-visakhapatnam",
    category: "Diabetology", 
    excerpt: "Leading diabetologist Dr. Marupalli Ravi Kishore from Sri Sai Sagar Clinics, Visakhapatnam, reveals breakthrough diabetes management achieving 90% glycemic control across Andhra Pradesh coastal regions including culturally adapted treatment approaches.",
    content: `Andhra Pradesh faces a diabetes epidemic with Visakhapatnam (530041) showing 18.2% prevalence. My revolutionary treatment protocols combining traditional Andhra dietary modifications with modern medicine achieve exceptional control rates across coastal AP.

## Comprehensive Diabetes Management

At Sri Sai Sagar Clinics, Visakhapatnam (530041), our diabetes care program achieves 90% glycemic control through culturally adapted treatment approaches and advanced monitoring systems.

**Contact Dr. Marupalli Ravi Kishore:**
- **Sri Sai Sagar Clinics, PM Palem, Visakhapatnam 530041**
- **Diabetes Helpline: 7337541113**
- **APMC Registration: 118337**
- **Specialization:** Diabetology, Endocrinology, Metabolic Disorders

Diabetes management requires comprehensive lifestyle modifications with expert medical supervision across coastal Andhra Pradesh.`,
    readTime: "13 min read",
    author: "Dr. Marupalli Ravi Kishore",
    date: "2025-01-01",
    tags: ["Diabetes", "Diabetology", "Visakhapatnam 530041", "Blood Sugar Management", "Andhra Pradesh"],
    icon: TestTube,
    color: "bg-blue-100 text-blue-600"
  },
  {
    id: 5,
    title: "Brain Stroke Emergency Care: Dr. Chintada Mounika's Neurological Excellence Saves Lives Across Visakhapatnam 530041",
    slug: "brain-stroke-emergency-care-dr-mounika-visakhapatnam-neurology",
    category: "Neurology",
    excerpt: "Expert neurologist Dr. Chintada Mounika from Sri Sai Sagar Clinics, Visakhapatnam (530041), shares critical stroke prevention and treatment protocols that have reduced stroke disability by 55% across coastal Andhra Pradesh regions.",
    content: `Brain stroke incidents in coastal Andhra Pradesh require immediate expert intervention. At Sri Sai Sagar Clinics, Visakhapatnam (530041), my neurology protocols have transformed stroke outcomes with rapid response systems serving the entire coastal AP region.

## Emergency Neurology Excellence

Stroke represents a leading cause of death and disability. Our comprehensive stroke care program at Sri Sai Sagar Clinics, Visakhapatnam (530041), provides 24/7 emergency neurological intervention with advanced treatment protocols.

**Contact Dr. Chintada Mounika:**
- **Sri Sai Sagar Clinics, PM Palem, Visakhapatnam 530041**
- **Stroke Emergency Hotline: 7337541113**
- **APMC Registration: 79912**
- **Specialization:** Neurology, Stroke Care, Neurological Rehabilitation

Stroke is a medical emergency where every minute counts. Quick recognition and immediate treatment ensure optimal recovery outcomes.`,
    readTime: "9 min read",
    author: "Dr. Chintada Mounika",
    date: "2025-01-01", 
    tags: ["Neurology", "Brain Stroke", "Visakhapatnam 530041", "Emergency Neurology", "Stroke Prevention"],
    icon: Brain,
    color: "bg-purple-100 text-purple-600"
  },
  {
    id: 6,
    title: "Advanced Dermatology & Cosmetology: Dr. Suggu Sreeramu's Skin Care Revolution in Visakhapatnam 530041",
    slug: "advanced-dermatology-cosmetology-dr-sreeramu-visakhapatnam",
    category: "Dermatology",
    excerpt: "Renowned dermatologist Dr. Suggu Sreeramu at Sri Sai Sagar Clinics, Visakhapatnam (530041), introduces cutting-edge skin treatments and cosmetology procedures specifically designed for Andhra Pradesh's coastal climate and lifestyle needs.",
    content: `Coastal Andhra Pradesh presents unique dermatological challenges due to high humidity and industrial pollution. My advanced treatments at Sri Sai Sagar Clinics, Visakhapatnam (530041), address these regional skin concerns with revolutionary approaches.

## Coastal Climate Dermatology Expertise

High humidity and industrial pollution in Visakhapatnam create unique skin challenges. Our comprehensive dermatology services provide advanced treatments adapted to coastal environmental conditions.

**Contact Dr. Suggu Sreeramu:**
- **Sri Sai Sagar Clinics, PM Palem, Visakhapatnam 530041**
- **Skin Care Consultation: 7337541113**
- **APMC Registration: 79856**
- **Specialization:** Dermatology, Cosmetology, Advanced Skin Treatments

Professional dermatological care addresses coastal climate challenges with evidence-based treatment protocols.`,
    readTime: "8 min read",
    author: "Dr. Suggu Sreeramu", 
    date: "2025-01-01",
    tags: ["Dermatology", "Cosmetology", "Visakhapatnam 530041", "Skin Care", "Coastal Climate"],
    icon: Star,
    color: "bg-yellow-100 text-yellow-600"
  },
  {
    id: 7,
    title: "Women's Health Excellence: Dr. Sahithi Royal's Gynecology & Obstetrics Expertise Transforms Maternal Care Visakhapatnam 530041",
    slug: "womens-health-gynecology-obstetrics-dr-sahithi-royal-visakhapatnam",
    category: "Gynecology",
    excerpt: "Leading gynecologist Dr. Sahithi Royal at Sri Sai Sagar Clinics, Visakhapatnam (530041), provides comprehensive women's healthcare including advanced obstetrics, fertility treatments, and maternal care across Andhra Pradesh coastal regions.",
    content: `Women's healthcare in coastal Andhra Pradesh requires specialized attention. At Sri Sai Sagar Clinics, Visakhapatnam (530041), I provide comprehensive gynecological and obstetric care with focus on maternal health and fertility solutions.

## Comprehensive Women's Healthcare

Our women's health program provides complete gynecological and obstetric care including advanced fertility treatments and maternal health services across coastal Andhra Pradesh.

**Contact Dr. Sahithi Royal:**
- **Sri Sai Sagar Clinics, PM Palem, Visakhapatnam 530041**
- **Women's Health Helpline: 7337541113**
- **APMC Registration: 79878**
- **Specialization:** Gynecology, Obstetrics, Fertility Medicine

Comprehensive women's healthcare ensures optimal maternal and reproductive health outcomes for coastal AP families.`,
    readTime: "10 min read",
    author: "Dr. Sahithi Royal",
    date: "2025-01-01",
    tags: ["Gynecology", "Obstetrics", "Women's Health", "Visakhapatnam 530041", "Maternal Care"],
    icon: Heart,
    color: "bg-rose-100 text-rose-600"
  },
  {
    id: 8,
    title: "Advanced Pulmonology & Emergency Care: Dr. R. Bharani's Respiratory Excellence at Shri Hospitals Guntur 522002",
    slug: "pulmonology-emergency-care-dr-bharani-shri-hospitals-guntur",
    category: "Pulmonology", 
    excerpt: "Expert pulmonologist Dr. R. Bharani at Shri Hospital, Guntur (522002), revolutionizes respiratory care with advanced treatments for asthma, COPD, and emergency lung conditions across Andhra Pradesh, providing 24/7 critical respiratory support.",
    content: `Respiratory health challenges in Andhra Pradesh require specialized care. At Shri Hospital, Guntur (522002), my pulmonology expertise addresses asthma, COPD, and emergency respiratory conditions with state-of-the-art treatment protocols.

## Advanced Respiratory Care Excellence

Comprehensive pulmonology services including emergency respiratory care, advanced lung function testing, and specialized treatments for asthma and COPD across Krishna district.

**Contact Dr. R. Bharani:**
- **Shri Hospital, Donka Road, Guntur 522002**
- **Respiratory Emergency: 7337541113**
- **APMC Registration: 79901**
- **Specialization:** Pulmonology, Emergency Medicine, Critical Respiratory Care

Expert respiratory care ensures optimal lung health and emergency intervention for breathing disorders.`,
    readTime: "9 min read",
    author: "Dr. R. Bharani",
    date: "2025-01-01",
    tags: ["Pulmonology", "Respiratory Care", "Guntur 522002", "Emergency Medicine", "Lung Health"],
    icon: Activity,
    color: "bg-green-100 text-green-600"
  },
  {
    id: 9,
    title: "Mental Health Revolution: Dr. Kona Sunil Kumar's Neuro-Psychiatry Excellence Transforms Lives in Tanuku 534211",
    slug: "mental-health-neuro-psychiatry-dr-sunil-kumar-tanuku",
    category: "Psychiatry",
    excerpt: "Leading neuro-psychiatrist Dr. Kona Sunil Kumar at Kona Hospital, Tanuku (534211), provides comprehensive mental health care addressing depression, anxiety, and neurological disorders across West Godavari and surrounding Andhra Pradesh regions.",
    content: `Mental health awareness in rural Andhra Pradesh needs expert attention. At Kona Hospital, Tanuku (534211), my neuro-psychiatry practice addresses depression, anxiety, and neurological disorders with culturally sensitive approaches for our communities.

## Comprehensive Mental Health Services

Specialized neuro-psychiatry care addressing depression, anxiety, and neurological disorders with culturally sensitive treatment approaches for West Godavari communities.

**Contact Dr. Kona Sunil Kumar:**
- **Kona Hospital, Tanuku 534211**
- **Mental Health Helpline: 7337541113**
- **APMC Registration: 79923**
- **Specialization:** Neuro-Psychiatry, Mental Health, Psychological Medicine

Professional mental health care ensures emotional wellbeing and neurological health across West Godavari region.`,
    readTime: "8 min read",
    author: "Dr. Kona Sunil Kumar",
    date: "2025-01-01",
    tags: ["Psychiatry", "Mental Health", "Tanuku 534211", "Depression", "Anxiety Treatment"],
    icon: Brain,
    color: "bg-indigo-100 text-indigo-600"
  },
  {
    id: 10,
    title: "Advanced Eye Care Excellence: Dr. S. Venkata Padmavathi's Ophthalmology & Cataract Expertise in Rajahmundry 533104", 
    slug: "eye-care-ophthalmology-cataract-dr-padmavathi-rajahmundry",
    category: "Ophthalmology",
    excerpt: "Expert ophthalmologist Dr. S. Venkata Padmavathi at Eye Care Center, Rajamahendravaram (533104), provides advanced cataract surgery and comprehensive eye care with latest technology serving East Godavari and surrounding Andhra Pradesh districts.",
    content: `Eye health challenges in East Godavari require specialized care. At Eye Care Center, Rajamahendravaram (533104), my ophthalmology practice offers advanced cataract surgery and comprehensive vision care with cutting-edge technology.

## Comprehensive Eye Care Services

Advanced ophthalmology services including cataract surgery, retinal treatments, and comprehensive vision care using latest technology for East Godavari communities.

**Contact Dr. S. Venkata Padmavathi:**
- **Eye Care Center, Rajamahendravaram 533104**
- **Eye Care Helpline: 7337541113**
- **APMC Registration: 79945**
- **Specialization:** Ophthalmology, Cataract Surgery, Retinal Medicine

Expert eye care ensures optimal vision health and advanced surgical treatments for all ocular conditions.`,
    readTime: "7 min read",
    author: "Dr. S. Venkata Padmavathi",
    date: "2025-01-01",
    tags: ["Ophthalmology", "Cataract Surgery", "Rajahmundry 533104", "Eye Care", "Vision Health"],
    icon: Eye,
    color: "bg-cyan-100 text-cyan-600"
  },
  {
    id: 11,
    title: "Surgical Excellence & Trauma Care: Dr. Viswa Teja Vaitla's Life-Saving Expertise at ARK Hospital Rajahmundry 533103",
    slug: "surgical-excellence-trauma-care-dr-vaitla-ark-hospital-rajahmundry", 
    category: "Surgery",
    excerpt: "Leading surgeon Dr. Viswa Teja Vaitla at ARK Hospital, Rajamahendravaram (533103), provides advanced general surgery and trauma care with minimally invasive techniques, serving emergency and elective surgical needs across East Godavari region.",
    content: `Surgical emergencies in East Godavari require immediate expert care. At ARK Hospital, Rajamahendravaram (533103), my surgical expertise covers general surgery, trauma care, and minimally invasive procedures with 24/7 availability.

## Advanced Surgical Excellence

Comprehensive surgical services including emergency trauma care, general surgery, and minimally invasive procedures with 24/7 availability across East Godavari region.

**Contact Dr. Viswa Teja Vaitla:**
- **ARK Hospital, Rajamahendravaram 533103**
- **Surgical Emergency: 7337541113**
- **APMC Registration: 79967**
- **Specialization:** General Surgery, Trauma Care, Minimally Invasive Surgery

Expert surgical care ensures optimal outcomes for emergency trauma and elective surgical procedures.`,
    readTime: "9 min read", 
    author: "Dr. Viswa Teja Vaitla",
    date: "2025-01-01",
    tags: ["General Surgery", "Trauma Care", "Rajahmundry 533103", "Emergency Surgery", "Minimally Invasive"],
    icon: Scissors,
    color: "bg-red-100 text-red-600"
  },
  {
    id: 12,
    title: "Advanced Medical Imaging: Dr. Vuyyuru Sasank's Radiology & Ultrasonography Excellence in Vijayawada 520008",
    slug: "medical-imaging-radiology-ultrasonography-dr-sasank-vijayawada",
    category: "Radiology",
    excerpt: "Expert radiologist Dr. Vuyyuru Sasank at Ramesh Hospital, Vijayawada (520008), provides advanced diagnostic imaging including MRI, CT scans, and specialized ultrasonography with AI-enhanced reporting serving Krishna district and surrounding regions.",
    content: `Accurate diagnostic imaging is crucial for effective treatment. At Ramesh Hospital, Vijayawada (520008), my radiology expertise provides comprehensive imaging services including advanced MRI, CT, and ultrasound with precise diagnostic reporting.

## Advanced Diagnostic Imaging Services

Comprehensive radiology services including MRI, CT scans, ultrasound, and AI-enhanced reporting for accurate diagnosis across Krishna district.

**Contact Dr. Vuyyuru Sasank:**
- **Ramesh Hospital, Vijayawada 520008**
- **Imaging Services: 7337541113**
- **APMC Registration: 79989**
- **Specialization:** Radiology, Medical Imaging, Diagnostic Medicine

Expert diagnostic imaging ensures accurate diagnosis with advanced technology and precise reporting.`,
    readTime: "8 min read",
    author: "Dr. Vuyyuru Sasank",
    date: "2025-01-01", 
    tags: ["Radiology", "Medical Imaging", "Vijayawada 520008", "Ultrasonography", "Diagnostic Imaging"],
    icon: Zap,
    color: "bg-blue-100 text-blue-600"
  },
  {
    id: 13,
    title: "Cancer Care Revolution: Dr. Manobhiram Boggavarapu's Oncology Excellence at Narayana Medical College Nellore 524003",
    slug: "cancer-care-oncology-dr-boggavarapu-narayana-medical-nellore",
    category: "Oncology", 
    excerpt: "Leading oncologist Dr. Manobhiram Boggavarapu at Narayana Medical College, Nellore (524003), provides comprehensive cancer treatment including surgical oncology, chemotherapy, and advanced cancer care protocols serving South Andhra Pradesh regions.",
    content: `Cancer treatment requires multidisciplinary expertise and compassionate care. At Narayana Medical College, Nellore (524003), my oncology practice provides comprehensive cancer care including surgical oncology and advanced treatment protocols.

## Comprehensive Cancer Care Excellence

Advanced oncology services including surgical oncology, chemotherapy, radiation therapy, and comprehensive cancer care protocols for South Andhra Pradesh.

**Contact Dr. Manobhiram Boggavarapu:**
- **Narayana Medical College, Nellore 524003**
- **Cancer Care Helpline: 7337541113**
- **APMC Registration: 80011**
- **Specialization:** Oncology, Surgical Oncology, Cancer Medicine

Expert cancer care provides comprehensive treatment with multidisciplinary approach and compassionate support.`,
    readTime: "11 min read",
    author: "Dr. Manobhiram Boggavarapu", 
    date: "2025-01-01",
    tags: ["Oncology", "Cancer Treatment", "Nellore 524003", "Surgical Oncology", "Chemotherapy"],
    icon: Shield,
    color: "bg-purple-100 text-purple-600"
  },
  {
    id: 14,
    title: "Digital Health Revolution: Dr. K Vigneswar Reddy's Telemedicine & Pharmacology Expertise in Hyderabad 500085",
    slug: "digital-health-telemedicine-pharmacology-dr-reddy-hyderabad",
    category: "Telemedicine",
    excerpt: "Digital health pioneer Dr. K Vigneswar Reddy at GetSet Healthy, Kukatpally, Hyderabad (500085), revolutionizes healthcare delivery through advanced telemedicine and pharmacology expertise, providing remote consultations across Telangana and Andhra Pradesh.",
    content: `Digital healthcare transformation is reshaping medical practice. At GetSet Healthy, Kukatpally, Hyderabad (500085), my telemedicine expertise combines pharmacology knowledge with digital platforms to provide accessible healthcare across Telugu states.

## Revolutionary Digital Healthcare

Advanced telemedicine services combining pharmacology expertise with digital platforms for accessible healthcare across Telangana and Andhra Pradesh.

**Contact Dr. K Vigneswar Reddy:**
- **GetSet Healthy, Kukatpally, Hyderabad 500085**
- **Telemedicine Helpline: 7337541113**
- **APMC Registration: 80033**
- **Specialization:** Telemedicine, Digital Health, Pharmacology

Digital health solutions provide accessible medical care through advanced telemedicine platforms and expert pharmacological guidance.`,
    readTime: "7 min read",
    author: "Dr. K Vigneswar Reddy",
    date: "2025-01-01",
    tags: ["Telemedicine", "Digital Health", "Hyderabad 500085", "Pharmacology", "Remote Healthcare"],
    icon: Phone,
    color: "bg-green-100 text-green-600"
  },
  {
    id: 15,
    title: "Advanced Skin & Laser Care: Dr. E. Venkata Ravikanth's Dermatology Excellence in Chittor 517001",
    slug: "advanced-skin-laser-care-dr-ravikanth-chittor-dermatology",
    category: "Dermatology",
    excerpt: "Expert dermatologist Dr. E. Venkata Ravikanth at Dr. Ravi's Skin Hair and Laser Clinic, Chittor (517001), provides advanced dermatological treatments including laser therapy, hair restoration, and comprehensive skin care for South Andhra Pradesh regions.",
    content: `Advanced dermatological care requires specialized expertise and modern technology. At Dr. Ravi's Skin Hair and Laser Clinic, Chittor (517001), my practice offers comprehensive skin treatments including laser therapy and cosmetic dermatology.

## Advanced Dermatology & Laser Medicine

Comprehensive dermatological services including laser therapy, hair restoration, and advanced skin treatments for South Andhra Pradesh communities.

**Contact Dr. E. Venkata Ravikanth:**
- **Dr. Ravi's Skin Hair and Laser Clinic, Chittor 517001**
- **Skin Care Consultation: 7337541113**
- **APMC Registration: 80055**
- **Specialization:** Dermatology, Laser Medicine, Hair Restoration

Expert dermatological care provides advanced skin treatments with modern laser technology and cosmetic procedures.`,
    readTime: "8 min read", 
    author: "Dr. E. Venkata Ravikanth",
    date: "2025-01-01",
    tags: ["Dermatology", "Laser Therapy", "Chittor 517001", "Hair Care", "Cosmetic Dermatology"],
    icon: Star,
    color: "bg-yellow-100 text-yellow-600"
  },
  {
    id: 16,
    title: "Advanced Sepsis & Critical Care: Dr. K Ramakrishna Prasad's Life-Support Excellence at Shri Hospitals Guntur 522002",
    slug: "advanced-sepsis-critical-care-dr-prasad-shri-hospitals-guntur",
    category: "Critical Care", 
    excerpt: "Critical care specialist Dr. K Ramakrishna Prasad at Shri Hospitals, Donka Road, Guntur (522002), provides advanced sepsis management and life-support systems with innovative protocols reducing ICU mortality across Krishna district.",
    content: `Sepsis and critical illness require immediate expert intervention. At Shri Hospitals, Guntur (522002), my critical care expertise focuses on sepsis management and advanced life-support with protocols that have significantly improved patient outcomes.

## Advanced Critical Care Medicine

Specialized sepsis management and critical care services with advanced life-support systems and innovative protocols for improved ICU outcomes.

**Contact Dr. K Ramakrishna Prasad:**
- **Shri Hospitals, Donka Road, Guntur 522002**
- **Critical Care Emergency: 7337541113**
- **APMC Registration: 80077**
- **Specialization:** Critical Care Medicine, Sepsis Management, Intensive Care

Expert critical care ensures optimal outcomes for sepsis and life-threatening conditions with advanced monitoring and treatment.`,
    readTime: "10 min read",
    author: "Dr. K Ramakrishna Prasad",
    date: "2025-01-01",
    tags: ["Critical Care", "Sepsis Management", "Guntur 522002", "Life Support", "ICU Care"],
    icon: ShieldAlert,
    color: "bg-red-100 text-red-600"
  },
  {
    id: 17,
    title: "Pediatric & Neonatal Excellence: Dr. Datla VSLN Raju's Child Care Revolution at Surya Hospital Bhimavaram 534202",
    slug: "pediatric-neonatal-excellence-dr-raju-surya-hospital-bhimavaram",
    category: "Neonatal Care",
    excerpt: "Leading pediatrician Dr. Datla VSLN Raju at Surya Hospital, Bhimavaram (534202), specializes in neonatal care and pediatric medicine, providing comprehensive child healthcare including NICU services for West Godavari and surrounding districts.",
    content: `Neonatal and pediatric care requires specialized expertise and advanced facilities. At Surya Hospital, Bhimavaram (534202), my practice focuses on newborn intensive care and comprehensive pediatric medicine with state-of-the-art NICU facilities.

## Specialized Neonatal & Pediatric Care

Advanced neonatal intensive care and comprehensive pediatric services with state-of-the-art NICU facilities for West Godavari communities.

**Contact Dr. Datla VSLN Raju:**
- **Surya Hospital, Bhimavaram 534202**
- **Pediatric Care Helpline: 7337541113**
- **APMC Registration: 80099**
- **Specialization:** Neonatal Care, Pediatrics, NICU Medicine

Expert neonatal and pediatric care ensures optimal health outcomes for newborns and children across West Godavari.`,
    readTime: "9 min read",
    author: "Dr. Datla VSLN Raju", 
    date: "2025-01-01",
    tags: ["Pediatrics", "Neonatal Care", "Bhimavaram 534202", "NICU", "Child Healthcare"],
    icon: Baby,
    color: "bg-pink-100 text-pink-600"
  },
  {
    id: 18,
    title: "Internal Medicine Excellence: Dr. DVSLN Krishnam Raju's Comprehensive Healthcare at Sunrise Hospitals Ganapavaram 534198",
    slug: "internal-medicine-excellence-dr-krishnam-raju-sunrise-hospitals-ganapavaram",
    category: "Internal Medicine",
    excerpt: "Expert internal medicine physician Dr. DVSLN Krishnam Raju at Sunrise Hospitals, Ganapavaram (534198), provides comprehensive internal medicine care including chronic disease management and preventive healthcare across West Godavari region.",
    content: `Internal medicine forms the foundation of comprehensive healthcare. At Sunrise Hospitals, Ganapavaram (534198), my internal medicine practice addresses chronic diseases, preventive care, and complex medical conditions with evidence-based approaches.

## Comprehensive Internal Medicine

Expert internal medicine care including chronic disease management, preventive healthcare, and complex medical condition treatment across West Godavari.

**Contact Dr. DVSLN Krishnam Raju:**
- **Sunrise Hospitals, Ganapavaram 534198**
- **Internal Medicine Consultation: 7337541113**
- **APMC Registration: 80121**
- **Specialization:** Internal Medicine, General Medicine, Preventive Care

Expert internal medicine provides comprehensive healthcare with evidence-based treatment for chronic and complex medical conditions.`,
    readTime: "8 min read", 
    author: "Dr. DVSLN Krishnam Raju",
    date: "2025-01-01",
    tags: ["Internal Medicine", "General Medicine", "Ganapavaram 534198", "Chronic Disease", "Preventive Care"],
    icon: Stethoscope,
    color: "bg-blue-100 text-blue-600"
  },
  {
    id: 19,
    title: "Community Health Excellence: Dr. C. Shravana Deepthi's Public Health Leadership at Apollo Medical College Chittor 517127",
    slug: "community-health-public-medicine-dr-deepthi-apollo-chittor",
    category: "Community Medicine",
    excerpt: "Community medicine specialist Dr. C. Shravana Deepthi at Apollo Medical College, Chittor (517127), leads public health initiatives including disease prevention, health education, and community wellness programs across South Andhra Pradesh rural regions.",
    content: `Community health and preventive medicine are essential for population wellness. At Apollo Medical College, Chittor (517127), my community medicine expertise focuses on public health programs, disease prevention, and health education initiatives.

## Community Health & Public Medicine

Comprehensive public health programs including disease prevention, health education, and community wellness initiatives for South Andhra Pradesh rural communities.

**Contact Dr. C. Shravana Deepthi:**
- **Apollo Medical College, Chittor 517127**
- **Public Health Programs: 7337541113**
- **APMC Registration: 80143**
- **Specialization:** Community Medicine, Public Health, Preventive Medicine

Expert community health leadership ensures population wellness through comprehensive public health programs and preventive medicine initiatives.`,
    readTime: "7 min read",
    author: "Dr. C. Shravana Deepthi",
    date: "2025-01-01", 
    tags: ["Community Medicine", "Public Health", "Chittor 517127", "Disease Prevention", "Health Education"],
    icon: Users,
    color: "bg-green-100 text-green-600"
  },
  {
    id: 20,
    title: "GEETH365 Healthcare Network: Comprehensive Medical Excellence Across 20 Doctors in 10 Cities of Andhra Pradesh & Telangana",
    slug: "geeth365-healthcare-network-comprehensive-medical-excellence-andhra-pradesh-telangana",
    category: "Healthcare Network",
    excerpt: "GEETH365's revolutionary healthcare network features 20 expert doctors across 10 strategic cities in Andhra Pradesh and Telangana, providing comprehensive medical specialties from cardiology to pediatrics with advanced treatments and 24/7 accessibility.",
    content: `GEETH365 has created the largest healthcare network in South India with 20 expert doctors across 10 cities including Visakhapatnam (530041), Rajahmundry (533103), Guntur (522002), Hyderabad (500085), and 6 other strategic locations, offering comprehensive medical care with WhatsApp booking and home consultations.

**Our Comprehensive Network:**
- **Visakhapatnam (530041)**: 7 specialists including cardiology, pediatrics, diabetology
- **Rajahmundry (533103-533104)**: 3 experts in critical care, surgery, ophthalmology  
- **Guntur (522002)**: 2 specialists in pulmonology and critical care
- **Hyderabad (500085)**: Telemedicine and digital health expertise
- **Additional Cities**: Chittor, Nellore, Vijayawada, Tanuku, Bhimavaram, Ganapavaram

**Services Available:**
- Emergency consultations 24/7
- WhatsApp booking: 7337541113
- Home consultations available
- All government insurance accepted
- Specialized treatments across 15+ medical fields

Contact GEETH365 for expert medical care across Andhra Pradesh and Telangana.`,
    readTime: "6 min read",
    author: "GEETH365 Medical Team", 
    date: "2025-01-01",
    tags: ["GEETH365", "Healthcare Network", "Andhra Pradesh", "Telangana", "Multi-specialty", "20 Doctors"],
    icon: TrendingUp,
    color: "bg-orange-100 text-orange-600"
  }
];

export const categories = [
  { id: 'all', name: 'All Posts', count: blogPosts.length },
  { id: 'Cardiology', name: 'Heart Care', count: 1 },
  { id: 'Critical Care', name: 'Critical Care', count: 1 },
  { id: 'Pediatrics', name: 'Child Care', count: 1 },
  { id: 'Diabetology', name: 'Diabetes Care', count: 1 },
  { id: 'Neurology', name: 'Brain Care', count: 1 },
  { id: 'Dermatology', name: 'Skin Care', count: 2 },
  { id: 'Gynecology', name: 'Women\'s Health', count: 1 },
  { id: 'Pulmonology', name: 'Lung Care', count: 1 },
  { id: 'Psychiatry', name: 'Mental Health', count: 1 },
  { id: 'Ophthalmology', name: 'Eye Care', count: 1 },
  { id: 'Surgery', name: 'Surgery', count: 1 },
  { id: 'Radiology', name: 'Medical Imaging', count: 1 },
  { id: 'Oncology', name: 'Cancer Care', count: 1 },
  { id: 'Telemedicine', name: 'Digital Health', count: 1 },
  { id: 'Neonatal Care', name: 'Newborn Care', count: 1 },
  { id: 'Internal Medicine', name: 'General Medicine', count: 1 },
  { id: 'Community Medicine', name: 'Public Health', count: 1 },
  { id: 'Healthcare Network', name: 'GEETH365 Network', count: 1 }
];